import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(str) {
  if (!str) return null;
  const diff = Date.now() - parseServerDate(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'zojuist';
  if (mins < 60) return `${mins} min. geleden`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} uur geleden`;
  return `${Math.floor(hrs / 24)} dagen geleden`;
}

function Badge({ color, children }) {
  const colors = {
    green: { bg: '#10b98120', text: '#10b981' },
    yellow: { bg: '#f59e0b20', text: '#d97706' },
    red: { bg: '#ef444420', text: '#ef4444' },
    blue: { bg: '#3b82f620', text: '#3b82f6' },
    gray: { bg: 'var(--color-border)', text: 'var(--color-text-muted)' },
  };
  const { bg, text } = colors[color] || colors.gray;
  return (
    <span style={`display:inline-block;font-size:10px;font-weight:700;padding:2px 7px;border-radius:10px;background:${bg};color:${text};white-space:nowrap`}>
      {children}
    </span>
  );
}

// ─── Settings panel ───────────────────────────────────────────────────────────

function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState({
    api_key: settings?.api_key || '',
    organization_id: settings?.organization_id || '',
    endpoint_url: settings?.endpoint_url || 'https://api.sportlink.com',
    is_enabled: !!settings?.is_enabled,
    auto_sync: !!settings?.auto_sync,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await api('/api/sportlink/settings', { method: 'PUT', body: JSON.stringify(form) });
      onSaved();
    } catch (e) { setError('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api('/api/sportlink/test', { method: 'POST' });
      setTestResult({ ok: true, message: res.message });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || 'Verbinding mislukt' });
    } finally { setTesting(false); }
  };

  return (
    <Card title="⚙️ Koppeling instellen">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">
            Verenigingsnummer <span style="color:var(--color-text-muted);font-weight:400">(org. ID)</span>
          </label>
          <input
            class="form-input"
            value={form.organization_id}
            onInput={e => set('organization_id', e.target.value)}
            placeholder="bv. 12345"
          />
        </div>
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">API-sleutel</label>
          <input
            class="form-input"
            type="password"
            value={form.api_key}
            onInput={e => set('api_key', e.target.value)}
            placeholder="Voer API-sleutel in (of typ DEMO voor testdata)"
          />
        </div>
      </div>

      <div style="margin-bottom:16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Endpoint URL</label>
        <input
          class="form-input"
          value={form.endpoint_url}
          onInput={e => set('endpoint_url', e.target.value)}
          placeholder="https://api.sportlink.com"
        />
        <p style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          Standaard: https://api.sportlink.com — pas aan voor andere bonden (KNHB, NEVOBO, etc.)
        </p>
      </div>

      <div style="display:flex;gap:24px;margin-bottom:20px">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px">
          <input type="checkbox" checked={form.is_enabled} onChange={e => set('is_enabled', e.target.checked)} style="width:15px;height:15px" />
          <span>Koppeling inschakelen</span>
        </label>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px">
          <input type="checkbox" checked={form.auto_sync} onChange={e => set('auto_sync', e.target.checked)} style="width:15px;height:15px" />
          <span>Automatisch synchroniseren (dagelijks)</span>
        </label>
      </div>

      {testResult && (
        <div style={`padding:10px 14px;border-radius:8px;font-size:13px;margin-bottom:14px;background:${testResult.ok ? '#10b98115' : '#ef444415'};color:${testResult.ok ? '#10b981' : '#ef4444'};border:1px solid ${testResult.ok ? '#10b98130' : '#ef444430'}`}>
          {testResult.ok ? '✅ ' : '❌ '}{testResult.message}
        </div>
      )}
      {error && <div class="alert alert-error mb-3">{error}</div>}

      <div style="display:flex;gap:8px">
        <Button variant="secondary" size="sm" onClick={handleTest} disabled={testing}>
          {testing ? 'Testen...' : '🔌 Verbinding testen'}
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Opslaan...' : 'Instellingen opslaan'}
        </Button>
      </div>

      <div style="margin-top:20px;padding:14px;background:var(--color-bg);border-radius:8px;border:1px solid var(--color-border)">
        <p style="font-size:12px;color:var(--color-text-muted);margin:0;line-height:1.6">
          💡 <strong>Geen API-sleutel?</strong> Typ <code style="background:var(--color-border);padding:1px 5px;border-radius:4px">DEMO</code> als API-sleutel om de koppeling te testen met voorbeeldleden. Vraag je echte API-sleutel aan via je bond of SportLink-beheerder. De koppeling werkt met KNVB, KNHB, NEVOBO en andere SportLink-bonden.
        </p>
      </div>
    </Card>
  );
}

// ─── Status card ──────────────────────────────────────────────────────────────

function StatusCard({ settings, totalMembers, linkedCount, onSync, syncing }) {
  const isConfigured = settings?.api_key && settings?.organization_id;
  const isEnabled = settings?.is_enabled;

  return (
    <Card title="📡 Synchronisatiestatus">
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:20px">
        {[
          { label: 'Leden in SportLink', value: totalMembers, color: '#6366f1' },
          { label: 'Gekoppeld aan account', value: linkedCount, color: '#10b981' },
          { label: 'Niet gekoppeld', value: totalMembers - linkedCount, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div key={label} style={`padding:14px;border-radius:10px;background:${color}10;border:1px solid ${color}30;text-align:center`}>
            <div style={`font-size:26px;font-weight:700;color:${color}`}>{value}</div>
            <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">{label}</div>
          </div>
        ))}
      </div>

      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px">
        <div style="font-size:13px;color:var(--color-text-muted)">
          {settings?.last_sync_at ? (
            <>
              Laatste sync: <strong>{timeAgo(settings.last_sync_at)}</strong>
              {settings.last_sync_status === 'error' && <span style="color:#ef4444;margin-left:8px">⚠ mislukt</span>}
            </>
          ) : 'Nog niet gesynchroniseerd'}
          {settings?.auto_sync ? <span style="margin-left:12px;font-size:11px;background:#10b98120;color:#10b981;padding:2px 7px;border-radius:10px">Automatisch aan</span> : null}
        </div>
        <Button
          variant={isConfigured && isEnabled ? 'primary' : 'secondary'}
          size="sm"
          onClick={onSync}
          disabled={syncing || !isConfigured || !isEnabled}
        >
          {syncing ? '🔄 Synchroniseren...' : '🔄 Nu synchroniseren'}
        </Button>
      </div>

      {!isConfigured && (
        <p style="font-size:12px;color:var(--color-text-muted);margin-top:12px;padding:10px;background:var(--color-bg);border-radius:6px">
          Vul eerst je verenigingsnummer en API-sleutel in om te synchroniseren.
        </p>
      )}
      {isConfigured && !isEnabled && (
        <p style="font-size:12px;color:#f59e0b;margin-top:12px">
          ⚠ Koppeling is uitgeschakeld. Zet "Koppeling inschakelen" aan om te synchroniseren.
        </p>
      )}
    </Card>
  );
}

// ─── Member row ───────────────────────────────────────────────────────────────

function MemberRow({ member, users, onLink, onAutoLink }) {
  const isLinked = !!member.linked_user_id;
  const isActive = member.member_status === 'actief';

  return (
    <div style="display:flex;align-items:center;gap:12px;padding:11px 16px;border-bottom:1px solid var(--color-border)">
      {/* Avatar */}
      <div style={`width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;background:${isLinked ? '#6366f1' : '#94a3b8'};flex-shrink:0`}>
        {(member.first_name?.[0] || '?')}{(member.last_name?.[0] || '')}
      </div>

      {/* Name + details */}
      <div style="flex:2;min-width:0">
        <div style="font-size:13px;font-weight:600">{member.first_name} {member.last_name}</div>
        <div style="font-size:11px;color:var(--color-text-muted)">
          {member.relation_number && <span>#{member.relation_number} · </span>}
          {member.email || <span style="color:var(--color-warning,#d97706)">geen e-mail</span>}
        </div>
      </div>

      {/* Status */}
      <div style="flex-shrink:0">
        <Badge color={isActive ? 'green' : 'gray'}>{isActive ? 'Actief' : 'Inactief'}</Badge>
      </div>

      {/* Member since */}
      <div style="font-size:11px;color:var(--color-text-muted);flex-shrink:0;min-width:70px">
        {member.member_since ? `Lid sinds ${member.member_since.slice(0,7)}` : '—'}
      </div>

      {/* Link status */}
      <div style="flex-shrink:0;min-width:140px">
        {isLinked ? (
          <div>
            <div style="font-size:11px;color:#10b981;font-weight:600">✅ Gekoppeld</div>
            <div style="font-size:10px;color:var(--color-text-muted)">{member.linked_user_name}</div>
          </div>
        ) : (
          <Badge color="yellow">Niet gekoppeld</Badge>
        )}
      </div>

      {/* Actions */}
      <div style="display:flex;gap:4px;flex-shrink:0">
        {!isLinked && member.email && (
          <button
            onClick={() => onAutoLink(member)}
            title="Auto-koppelen op e-mail"
            style="padding:4px 9px;font-size:11px;border:1px solid #6366f1;border-radius:6px;background:transparent;color:#6366f1;cursor:pointer;white-space:nowrap"
          >
            🔗 Auto
          </button>
        )}
        <button
          onClick={() => onLink(member)}
          title={isLinked ? 'Koppeling wijzigen' : 'Handmatig koppelen'}
          style="padding:4px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
        >
          {isLinked ? '✏️' : '👤'}
        </button>
      </div>
    </div>
  );
}

// ─── Link modal ───────────────────────────────────────────────────────────────

function LinkModal({ member, onClose, onLinked }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(member.linked_user_id || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/api/users').then(data => setUsers(Array.isArray(data) ? data : data.users || [])).catch(() => {});
  }, []);

  const filtered = search
    ? users.filter(u => u.display_name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
    : users;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(`/api/sportlink/members/${member.id}/link`, { method: 'POST', body: JSON.stringify({ userId: selected }) });
      onLinked();
      onClose();
    } catch { setSaving(false); }
  };

  return (
    <Modal isOpen title={`Koppel ${member.first_name} ${member.last_name}`} onClose={onClose}>
      <p style="font-size:13px;color:var(--color-text-muted);margin-bottom:14px">
        Kies welk gebruikersaccount overeenkomt met dit SportLink-lid.
        {member.email && <span> SportLink e-mail: <strong>{member.email}</strong></span>}
      </p>

      <input
        class="form-input"
        value={search}
        onInput={e => setSearch(e.target.value)}
        placeholder="Zoek gebruiker..."
        style="margin-bottom:10px"
      />

      <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden;max-height:280px;overflow-y:auto;margin-bottom:16px">
        <label style="display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px;background:var(--color-bg)">
          <input type="radio" name="user" value="" checked={!selected} onChange={() => setSelected(null)} />
          <span style="color:var(--color-text-muted)">— Ontkoppelen —</span>
        </label>
        {filtered.map(u => (
          <label key={u.id} style="display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px">
            <input type="radio" name="user" value={u.id} checked={selected === u.id} onChange={() => setSelected(u.id)} />
            <div>
              <div style="font-weight:500">{u.display_name}</div>
              <div style="font-size:11px;color:var(--color-text-muted)">{u.email}</div>
            </div>
          </label>
        ))}
        {filtered.length === 0 && <p style="padding:16px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen gebruikers gevonden</p>}
      </div>

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Opslaan...' : 'Koppeling opslaan'}</Button>
      </div>
    </Modal>
  );
}

// ─── Members panel ────────────────────────────────────────────────────────────

function MembersPanel({ members, onRefresh }) {
  const [search, setSearch] = useState('');
  const [filterLinked, setFilterLinked] = useState('all');
  const [filterStatus, setFilterStatus] = useState('actief');
  const [linkMember, setLinkMember] = useState(null);
  const [autoLinking, setAutoLinking] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);

  const filtered = members.filter(m => {
    if (filterStatus && m.member_status !== filterStatus) return false;
    if (filterLinked === 'linked' && !m.linked_user_id) return false;
    if (filterLinked === 'unlinked' && m.linked_user_id) return false;
    if (search) {
      const s = search.toLowerCase();
      return (m.first_name + ' ' + m.last_name).toLowerCase().includes(s) ||
        (m.email || '').toLowerCase().includes(s) ||
        (m.relation_number || '').includes(s);
    }
    return true;
  });

  const handleAutoLink = async (member) => {
    try {
      await api(`/api/sportlink/members/${member.id}/auto-link`, { method: 'POST' });
      onRefresh();
    } catch (e) {
      alert(e.message || 'Automatisch koppelen mislukt');
    }
  };

  const handleAutoLinkAll = async () => {
    setAutoLinking(true); setAutoLinkResult(null);
    try {
      const res = await api('/api/sportlink/auto-link-all', { method: 'POST' });
      setAutoLinkResult(`✅ ${res.total_linked} leden gekoppeld op basis van e-mailadres`);
      onRefresh();
    } catch { setAutoLinkResult('❌ Automatisch koppelen mislukt'); }
    finally { setAutoLinking(false); }
  };

  return (
    <Card title={`👥 Ledenbestand (${members.length})`}>
      {/* Toolbar */}
      <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px">
        <div style="position:relative;flex:1;min-width:200px;max-width:280px">
          <span style="position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:13px;pointer-events:none;color:var(--color-text-muted)">🔍</span>
          <input class="form-input" value={search} onInput={e => setSearch(e.target.value)} placeholder="Zoek op naam, e-mail..." style="padding-left:30px;height:34px;font-size:13px" />
        </div>

        <select class="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style="height:34px;font-size:13px;min-width:120px">
          <option value="">Alle statussen</option>
          <option value="actief">Actief</option>
          <option value="inactief">Inactief</option>
        </select>

        <select class="form-input" value={filterLinked} onChange={e => setFilterLinked(e.target.value)} style="height:34px;font-size:13px;min-width:140px">
          <option value="all">Alle leden</option>
          <option value="linked">Wel gekoppeld</option>
          <option value="unlinked">Niet gekoppeld</option>
        </select>

        <Button variant="secondary" size="sm" onClick={handleAutoLinkAll} disabled={autoLinking}>
          {autoLinking ? 'Bezig...' : '🔗 Alles auto-koppelen'}
        </Button>
      </div>

      {autoLinkResult && (
        <div style="padding:10px 14px;border-radius:8px;font-size:13px;margin-bottom:12px;background:var(--color-bg);border:1px solid var(--color-border)">
          {autoLinkResult}
        </div>
      )}

      {filtered.length === 0 ? (
        <div style="padding:40px;text-align:center;color:var(--color-text-muted);font-size:13px">
          {members.length === 0
            ? '📭 Nog geen leden gesynchroniseerd — klik op "Nu synchroniseren" om te starten'
            : 'Geen leden gevonden voor deze filters'}
        </div>
      ) : (
        <div>
          {/* Header */}
          <div style="display:flex;align-items:center;gap:12px;padding:8px 16px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);border-bottom:2px solid var(--color-border)">
            <div style="width:34px;flex-shrink:0" />
            <div style="flex:2">Lid</div>
            <div style="flex-shrink:0;width:60px">Status</div>
            <div style="flex-shrink:0;min-width:70px">Lid sinds</div>
            <div style="flex-shrink:0;min-width:140px">Koppeling</div>
            <div style="flex-shrink:0;width:100px">Acties</div>
          </div>
          {filtered.map(m => (
            <MemberRow
              key={m.id}
              member={m}
              onLink={setLinkMember}
              onAutoLink={handleAutoLink}
            />
          ))}
          <div style="padding:8px 16px;font-size:12px;color:var(--color-text-muted)">
            {filtered.length} van {members.length} leden
          </div>
        </div>
      )}

      {linkMember && (
        <LinkModal
          member={linkMember}
          onClose={() => setLinkMember(null)}
          onLinked={onRefresh}
        />
      )}
    </Card>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function SportlinkView() {
  const [settings, setSettings] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        api('/api/sportlink/settings'),
        api('/api/sportlink/members'),
      ]);
      setSettings(s);
      setMembers(m);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const res = await api('/api/sportlink/sync', { method: 'POST' });
      setSyncMsg({ ok: true, text: `✅ ${res.synced} leden gesynchroniseerd` });
      await load();
    } catch (e) {
      setSyncMsg({ ok: false, text: `❌ ${e.message || 'Synchronisatie mislukt'}` });
    } finally { setSyncing(false); }
  };

  const linkedCount = members.filter(m => m.linked_user_id).length;

  return (
    <div>
      <div class="page-header" style="margin-bottom:24px">
        <div>
          <h1 class="page-title" style="display:flex;align-items:center;gap:10px">
            <img src="https://www.sportlink.com/favicon.ico" style="width:22px;height:22px;border-radius:4px" onError={e => e.target.style.display='none'} />
            SportLink koppeling
          </h1>
          <p style="font-size:13px;color:var(--color-text-muted);margin-top:2px">
            Synchroniseer je ledenbestand vanuit SportLink en koppel leden aan gebruikersaccounts.
          </p>
        </div>
        {settings?.is_enabled ? (
          <Badge color="green">Koppeling actief</Badge>
        ) : (
          <Badge color="gray">Koppeling inactief</Badge>
        )}
      </div>

      {syncMsg && (
        <div style={`padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:20px;background:${syncMsg.ok ? '#10b98115' : '#ef444415'};color:${syncMsg.ok ? '#10b981' : '#ef4444'};border:1px solid ${syncMsg.ok ? '#10b98130' : '#ef444430'}`}>
          {syncMsg.text}
        </div>
      )}

      {loading ? (
        <div style="padding:60px;text-align:center;color:var(--color-text-muted)">Laden...</div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:20px">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
            <SettingsPanel settings={settings} onSaved={load} />
            <StatusCard
              settings={settings}
              totalMembers={members.length}
              linkedCount={linkedCount}
              onSync={handleSync}
              syncing={syncing}
            />
          </div>
          <MembersPanel members={members} onRefresh={load} />
        </div>
      )}
    </div>
  );
}
